// API 客户端 - 所有请求走环境变量配置的 API_BASE_URL
// 域名迁移时只需修改 VITE_API_BASE_URL，无需改业务代码
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
const STREAM_BASE_URL = API_BASE_URL; // SSE 同源（cookie/CORS 一致）

const TOKEN_KEY = 'hei-ge-token';
const USER_KEY = 'hei-ge-user';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export interface AuthUser {
  id: string;
  username: string;
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setStoredUser(user: AuthUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // 带 X-Requested-With 让 Cloudflare Browser Integrity Check 更倾向放行
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json, text/plain, */*',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: 'omit',
    });
  } catch (networkErr) {
    // 网络层失败（如 DNS / CORS / offline）— 给用户明确的网络提示
    throw new ApiError(0, `网络错误：${(networkErr as Error).message}（请检查网络或稍后重试）`);
  }

  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    let body: any = {};
    try { body = JSON.parse(raw); } catch {}
    // Cloudflare 错误（如 1010）通常带 detail 字段，把可读消息透传给前端
    const cfCode = body?.error_code;
    const cfDetail = body?.detail;
    if (cfCode && cfDetail) {
      throw new ApiError(res.status, `Cloudflare 拦截（${cfCode}）：${cfDetail}`);
    }
    throw new ApiError(res.status, body?.error ?? body?.message ?? raw ?? res.statusText);
  }
  return res.json();
}

/** 包装：将 { account } / { message, account } 等响应统一转成 { ok, message, account } */
function unwrap<T = any>(promise: Promise<any>): Promise<{ ok: boolean; message: string; account?: any; [k: string]: any }> {
  return promise.then((r) => ({
    ok: true,
    message: r?.message ?? 'ok',
    ...r,
  })).catch((e: ApiError) => ({ ok: false, message: e.message }));
}

export const api = {
  // ============ Auth ============
  register: (username: string, password: string) =>
    request<{ token: string; user: AuthUser }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  login: (username: string, password: string) =>
    request<{ token: string; user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  // ============ Account ============
  getAccount: () => request<{ account: any }>('/api/account'),
  signin: () => request<{ account: any }>('/api/account/signin', { method: 'POST' }),
  reset: () => request<{ account: any }>('/api/account/reset', { method: 'POST' }),

  // ============ Trading ============
  buy: (params: any) =>
    request<{ account: any }>('/api/trading/buy', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
  sell: (params: any) =>
    request<{ account: any }>('/api/trading/sell', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // ============ Contract ============
  openContract: (params: any) =>
    request<{ contract: any; account: any }>('/api/contract/open', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
  closeContract: (contractId: string, price: number) =>
    request<{ account: any }>('/api/contract/close', {
      method: 'POST',
      body: JSON.stringify({ contractId, price }),
    }),
  setContractTPSL: (contractId: string, takeProfit?: number, stopLoss?: number) =>
    request<{ account: any }>('/api/contract/tpsl', {
      method: 'POST',
      body: JSON.stringify({ contractId, takeProfit, stopLoss }),
    }),
  contractMark: (marks: Record<string, number>) =>
    request<{ contracts: any[] }>('/api/contract/mark', {
      method: 'POST',
      body: JSON.stringify({ marks }),
    }),
  liquidateCheck: (marks: Record<string, number>) =>
    request<{ liquidated: any[] }>('/api/contract/liquidate-check', {
      method: 'POST',
      body: JSON.stringify({ marks }),
    }),
  getContractPositions: () => request<{ contracts: any[]; contractTrades: any[] }>('/api/contract/positions'),

  // ============ Insurance ============
  buyBasic: () =>
    request<{ account: any }>('/api/insurance/buy-basic', { method: 'POST' }),
  buyFinance: (tier: 'tier1' | 'tier2' | 'tier3') =>
    request<{ account: any }>('/api/insurance/buy-finance', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    }),
  buyGuard: () =>
    request<{ account: any }>('/api/insurance/buy-guard', { method: 'POST' }),
  setAutoInsurance: (params: { enabled?: boolean; cashRatio?: number; tier?: any }) =>
    request<{ account: any }>('/api/insurance/auto', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // ============ K Line（公开）============
  getKlineHistory: (params: { code: string; market: string; type: string }) =>
    request<{ candles: any[] }>(`/api/kline/history?code=${params.code}&market=${params.market}&type=${params.type}`),

  // ============ 股票搜索（公开）============
  searchStocks: (q: string) =>
    request<{ results: any[]; count: number; q: string }>(
      `/api/stocks/search?q=${encodeURIComponent(q)}`
    ),

  // ============ 实时行情（公开）============
  getRealtimeQuotes: (items: { code: string; market: string; name?: string }[]) =>
    request<{ quotes: any[]; count: number; fetchedAt: number }>(
      `/api/quotes/realtime?items=${encodeURIComponent(JSON.stringify(items))}`
    ),

  // ============ 股票池（公开）============
  // 服务端维护的默认股票池（之前硬编码在前端，已迁移到 /api/pool）
  getPool: () =>
    request<{ pool: import('../shared/types').StockBasic[]; count: number; fetchedAt: number }>(
      '/api/pool'
    ),

  // ============ Heige ============
  getHeigeHistory: () => request<{ events: any[] }>('/api/heige/history'),
  getHeigeImpacts: () => request<{ impacts: any[] }>('/api/heige/impacts'),
  triggerHeige: (body: any) =>
    request<{ event: any; result: any }>('/api/heige/trigger', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const SSE_CONFIG = {
  baseUrl: STREAM_BASE_URL,
  subscribeUrl: () => `${STREAM_BASE_URL}/api/stream/subscribe?token=${getToken() ?? ''}`,
  recentUrl: () => `${STREAM_BASE_URL}/api/stream/recent`,
};